import { useEffect, useReducer, useRef } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ascleo/ui/lib/utils'

/**
 * Props of {@link ScrollContainer}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const props: ScrollContainerProps = { header: null, footer: undefined, children: null }
 * ```
 */
export interface ScrollContainerProps {
  children: React.ReactNode
  header: React.ReactNode
  footer: (() => React.ReactNode | undefined) | undefined
  className?: string
}

interface ScrollMetrics {
  isScrolled: boolean
  showFooterBorder: boolean
  isOverflowing: boolean
  isAtTop: boolean
  isAtBottom: boolean
}

type ScrollMetricsAction = { type: 'measure'; metrics: ScrollMetrics }

const INITIAL_SCROLL_METRICS: ScrollMetrics = {
  isScrolled: false,
  showFooterBorder: false,
  isOverflowing: false,
  isAtTop: true,
  isAtBottom: false
}

function scrollMetricsEqual(left: ScrollMetrics, right: ScrollMetrics): boolean {
  return (
    left.isScrolled === right.isScrolled &&
    left.showFooterBorder === right.showFooterBorder &&
    left.isOverflowing === right.isOverflowing &&
    left.isAtTop === right.isAtTop &&
    left.isAtBottom === right.isAtBottom
  )
}

function scrollMetricsReducer(state: ScrollMetrics, action: ScrollMetricsAction): ScrollMetrics {
  switch (action.type) {
    case 'measure':
      return scrollMetricsEqual(state, action.metrics) ? state : action.metrics
  }
}

/**
 * Kiosk shell frame: a `<header>`, a scrolling `<main>` and an optional
 * `<footer>` landmark (FC-22). While the main area overflows, two labelled
 * nudge buttons (press-and-hold to scroll; Enter/Space scrolls by a page)
 * appear in a gutter that is reserved permanently and symmetrically, so the
 * content stays centred and does not reflow when scrolling becomes necessary
 * (KO-10, SPK-05, FC-31). A nudge button at its end of travel is hidden from
 * view, from the tab order and from assistive technology (FC-23).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <ScrollContainer header={<KioskHeader />} footer={() => <KioskFooter />}>
 *   <Step />
 * </ScrollContainer>
 * ```
 */
export function ScrollContainer({ children, header, footer, className }: ScrollContainerProps) {
  const { t } = useTranslation()
  const [scrollMetrics, dispatchScrollMetrics] = useReducer(
    scrollMetricsReducer,
    INITIAL_SCROLL_METRICS
  )
  const containerRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollIntervalRef = useRef<number | undefined>(undefined)
  const { isScrolled, showFooterBorder, isOverflowing, isAtTop, isAtBottom } = scrollMetrics

  useEffect(function runScrollTopEffect() {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    const handleScrollAndResize = () => {
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight
      const nextIsAtBottom = scrollHeight - scrollTop <= clientHeight + 1
      const nextIsOverflowing = scrollHeight > clientHeight

      dispatchScrollMetrics({
        type: 'measure',
        metrics: {
          isScrolled: scrollTop > 0,
          isAtTop: scrollTop === 0,
          isAtBottom: nextIsAtBottom,
          isOverflowing: nextIsOverflowing,
          showFooterBorder: nextIsOverflowing && !nextIsAtBottom
        }
      })
    }

    container.addEventListener('scroll', handleScrollAndResize, {
      passive: true
    })
    const resizeObserver = new ResizeObserver(handleScrollAndResize)
    resizeObserver.observe(container)

    const mutationObserver = new MutationObserver(() => {
      setTimeout(handleScrollAndResize, 0)
    })
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    })

    return function unbindEventListeners() {
      container.removeEventListener('scroll', handleScrollAndResize)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [])

  const startScroll = (direction: 'up' | 'down', event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    const container = containerRef.current
    if (!container) return

    const scroll = () => {
      container.scrollBy({
        top: direction === 'up' ? -20 : 20,
        behavior: 'auto'
      })
    }

    scroll()
    scrollIntervalRef.current = window.setInterval(scroll, 50)
  }

  // Keyboard activation (Enter/Space) reports `detail === 0`; pointer
  // activation is already handled by the press-and-hold handlers.
  const scrollByPage = (direction: 'up' | 'down', event: React.MouseEvent) => {
    if (event.detail !== 0) return
    const container = containerRef.current
    if (!container) return
    const distance = Math.max(container.clientHeight * 0.8, 80)
    container.scrollBy({ top: direction === 'up' ? -distance : distance, behavior: 'smooth' })
  }

  const stopScroll = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = undefined
    }
  }

  const footerContent = footer?.()

  return (
    <div
      id="scroll-container-wrapper"
      className={cn(
        'scroll-container relative grid h-full min-h-0 min-w-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-x-clip',
        className
      )}
    >
      <header
        id="scroll-container-header"
        className={cn(
          'z-10 p-4 bg-transparent backdrop-blur-xl bg-background/60',
          isScrolled && 'border-b border-border shadow-lg'
        )}
      >
        {header}
      </header>
      {/* The scroll row is its own positioning context so the nudge buttons are
          centred on the scrollable area. Their gutter is reserved on both sides
          at all times: content is centred and never reflows when it starts to
          overflow. */}
      <div className="relative min-h-0 min-w-0">
        <main
          ref={containerRef}
          id="scroll-container"
          className="h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden px-14 md:px-[4.5rem]"
        >
          <div
            id="scroll-container-content"
            ref={contentRef}
            className="grid min-h-full min-w-0 grid-rows-[minmax(0,1fr)] overflow-x-hidden"
          >
            {children}
          </div>
        </main>

        {isOverflowing && (
          <div className="absolute end-1 top-1/2 z-20 flex -translate-y-1/2 flex-col gap-2 md:end-2">
            <ScrollNudgeButton
              testId="scroll-up-button"
              label={t('common.scroll-up')}
              atEnd={isAtTop}
              onPressStart={(event) => startScroll('up', event)}
              onPressEnd={stopScroll}
              onKeyboardActivate={(event) => scrollByPage('up', event)}
            >
              <ChevronUp aria-hidden className="size-7 md:size-8" />
            </ScrollNudgeButton>
            <ScrollNudgeButton
              testId="scroll-down-button"
              label={t('common.scroll-down')}
              atEnd={isAtBottom}
              onPressStart={(event) => startScroll('down', event)}
              onPressEnd={stopScroll}
              onKeyboardActivate={(event) => scrollByPage('down', event)}
            >
              <ChevronDown aria-hidden className="size-7 md:size-8" />
            </ScrollNudgeButton>
          </div>
        )}
      </div>
      {footerContent && (
        <footer
          id="scroll-container-footer"
          className={cn(
            'z-10 p-4 bg-transparent backdrop-blur-xl bg-background/60',
            showFooterBorder && 'border-t border-border shadow-[0_-4px_12px_-1px_rgba(0,0,0,0.1)]'
          )}
        >
          <div className="flex justify-between w-full min-w-0 gap-4">{footerContent}</div>
        </footer>
      )}
    </div>
  )
}

function ScrollNudgeButton({
  testId,
  label,
  atEnd,
  onPressStart,
  onPressEnd,
  onKeyboardActivate,
  children
}: {
  testId: string
  label: string
  /** The scroll position is at this button's end of travel: the button is inert and invisible. */
  atEnd: boolean
  onPressStart: (event: React.MouseEvent | React.TouchEvent) => void
  onPressEnd: (event: React.MouseEvent | React.TouchEvent) => void
  onKeyboardActivate: (event: React.MouseEvent) => void
  children: React.ReactNode
}) {
  return (
    <button
      data-testid={testId}
      type="button"
      aria-label={label}
      aria-controls="scroll-container"
      aria-hidden={atEnd ? true : undefined}
      tabIndex={atEnd ? -1 : undefined}
      className={cn(
        'flex size-12 items-center justify-center rounded-full border border-border bg-background/80 p-1 shadow-lg backdrop-blur-md hover:bg-background/90 md:size-14',
        atEnd && 'invisible pointer-events-none'
      )}
      onMouseDown={onPressStart}
      onMouseUp={onPressEnd}
      onMouseLeave={onPressEnd}
      onTouchStart={onPressStart}
      onTouchEnd={onPressEnd}
      onClick={onKeyboardActivate}
    >
      {children}
    </button>
  )
}
